import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavContext } from "../context/NavContext";
import Nav from "./Nav";
import axios from "axios";
import { collection, onSnapshot, doc, updateDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import Modal from "react-modal";
import Swal from "sweetalert2";
import Paginador from "./Paginador";
import Spinner from "./Spinner";
import { useLocation } from "react-router-dom";
import FacturaDetallesModal from "./FacturaDetallesModal";
import { useFunctionsPrevia } from "../hooks/useFunctionsPrevia"; 

// Configuración opcional para el modal
Modal.setAppElement("#root");

const customStyles = {
  content: {
    maxWidth: "600px",
    margin: "auto",
    padding: "20px",
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    border: "none",
    fontFamily: "Roboto, sans-serif",
    "::WebkitScrollbar": {
      width: "0px",
      background: "transparent",
    },
    msOverflowStyle: "none",
    scrollbarWidth: "none",
  },
  overlay: {
    backgroundColor: "rgba(0,0,0,0.5)",
    zIndex: 130,
  },
  input: {
    width: "100%",
    padding: "8px",
    boxSizing: "border-box",
    margin: "10px 0",
  },
  fileInput: {
    width: "100%",
    margin: "10px 0",
    display: "block",
  },
  select: {
    width: "100%",
    padding: "8px",
    boxSizing: "border-box",
    margin: "10px 0",
  },
  button: {
    width: "30%",
    padding: "10px",
    backgroundColor: "#007bff",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    fontSize: "1rem",
    cursor: "pointer",
  },
};

const FacturasClientes = () => {
  const { proximasAVencer, proximosEventos, searchTerm, setSearchTerm } =
    useNavContext();
  const location = useLocation();
  const [selectedFactura, setSelectedFactura] = useState(null);
  const [detalleModalOpen, setDetalleModalOpen] = useState(false);
  const [facturasClientes, setFacturasClientes] = useState([]);
  const [fileCer, setFileCer] = useState(null);
  const [fileKey, setFileKey] = useState(null);
  const [password, setPassword] = useState("");
  const [rfc, setRfc] = useState("");
  const [fechaInicial, setFechaInicial] = useState("");
  const [fechaFinal, setFechaFinal] = useState("");
  const [tipo, setTipo] = useState("Emitidas");
  const [loading, setLoading] = useState(false);
  const [estadosCeldas, setEstadosCeldas] = useState({});
  const [showNuevoButton, setShowNuevoButton] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [solicitudPendienteId, setSolicitudPendienteId] = useState("");
  const [ordenamiento, setOrdenamiento] = useState({
    campo: "Folio",
    ascendente: true,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [loadingFacturas, setLoadingFacturas] = useState(true);
  const { formatDate } = useFunctionsPrevia();

  // Limpia el buscador al cambiar de ruta
  useEffect(() => {
    setSearchTerm("");
  }, [location.pathname, setSearchTerm]);

  useEffect(() => {
    if (solicitudPendienteId) {
      console.log("Solicitud pendiente ID:", solicitudPendienteId);
    }
  }, [solicitudPendienteId]);

  const openDetalleModal = (factura) => {
    setSelectedFactura(factura);
    setDetalleModalOpen(true);
  };

  const closeDetalleModal = () => {
    setDetalleModalOpen(false);
    setSelectedFactura(null);
  };

  const openModal = () => {
    setModalOpen(true);
    setShowNuevoButton(false);
  };

  const closeModal = () => {
    setModalOpen(false);
    setShowNuevoButton(true);
  };

  // Escucha en tiempo real la colección de facturasEmitidas
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "facturasEmitidas"),
      (snapshot) => {
        const dataObtenida = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setFacturasClientes(dataObtenida);
        const initialEstados = {};
        dataObtenida.forEach((factura) => {
          initialEstados[factura.id] = factura.estado || "Pendiente";
        });
        setEstadosCeldas(initialEstados);
        setLoadingFacturas(false); // <-- Marcar que ya terminó la carga
      },
      (error) => {
        console.error("Error al obtener facturasEmitidas:", error);
        setLoadingFacturas(false);
      }
    );
    return () => unsubscribe();
  }, []);

  function generateDateSearchVariants(dateString) {
    // Convierte el string a Date
    const date = new Date(dateString);
    if (isNaN(date)) return [];

    // Extrae día, mes, año
    const day = date.getDate().toString().padStart(2, "0");
    const shortMonths = [
      "Ene",
      "Feb",
      "Mar",
      "Abr",
      "May",
      "Jun",
      "Jul",
      "Ago",
      "Sep",
      "Oct",
      "Nov",
      "Dic",
    ];
    const longMonths = [
      "enero",
      "febrero",
      "marzo",
      "abril",
      "mayo",
      "junio",
      "julio",
      "agosto",
      "septiembre",
      "octubre",
      "noviembre",
      "diciembre",
    ];
    const monthShort = shortMonths[date.getMonth()];
    const monthLong = longMonths[date.getMonth()];
    const year = date.getFullYear();

    const variants = [
      `${day} ${monthShort} ${year}`, // coincide con lo que muestras en pantalla
      `${day} ${monthLong} ${year}`, // mes completo
      `${day} de ${monthLong} ${year}`, // con la palabra "de"
    ];

    // Retorna todas en minúsculas para el filtrado
    return variants.map((v) => v.toLowerCase());
  }

  // Eliminamos duplicados basado en UUID o llave compuesta
  const uniqueFacturas = useMemo(() => {
    const seenKeys = new Set();
    const result = [];
    for (const factura of facturasClientes) {
      const uuid = factura.UUID ? factura.UUID.trim() : "";
      const folio = factura.Folio ? factura.Folio.trim() : "";
      const receptorRfc = factura.receptor_Rfc
        ? factura.receptor_Rfc.trim()
        : "";
      const tipoCfdi = factura.TipoDeComprobante
        ? factura.TipoDeComprobante.trim()
        : "";
      let fechaStr = "";
      if (factura.Fecha) {
        const fechaObj = new Date(factura.Fecha);
        if (!isNaN(fechaObj.getTime())) {
          fechaStr = fechaObj.toISOString().split("T")[0];
        }
      }
      let uniqueKey = uuid;
      if (!uniqueKey) {
        uniqueKey = `${folio}-${receptorRfc}-${tipoCfdi}-${fechaStr}`;
      }
      if (!seenKeys.has(uniqueKey)) {
        seenKeys.add(uniqueKey);
        result.push(factura);
      }
    }
    return result;
  }, [facturasClientes]);

  // Filtrado de búsqueda
  const filteredFacturasClientes = useMemo(() => {
    if (!uniqueFacturas || uniqueFacturas.length === 0) return [];

    // Término de búsqueda en minúsculas
    const searchLower = searchTerm.toLowerCase();

    // 1) Filtrar únicamente los Ingresos
    const ingresos = uniqueFacturas.filter((f) => f.TipoDeComprobante === "I");

    // 2) Aplicar búsqueda sobre ese subconjunto
    return ingresos.filter((factura) =>
      Object.keys(factura).some((col) => {
        const campo = factura[col];
        if (campo == null) return false;

        // Si es Fecha, generamos variantes para buscar
        if (col === "Fecha") {
          const variants = generateDateSearchVariants(campo);
          if (variants.some((variant) => variant.includes(searchLower))) {
            return true;
          }
        }

        // Resto de columnas
        const valor =
          typeof campo === "object" ? JSON.stringify(campo) : campo.toString();
        return valor.toLowerCase().includes(searchLower);
      })
    );
  }, [uniqueFacturas, searchTerm]);

  const handleOrdenamientoChange = (campo) => {
    setOrdenamiento((prev) => {
      if (prev.campo === campo) {
        return { campo, ascendente: !prev.ascendente };
      } else {
        return { campo, ascendente: true };
      }
    });
  };

  const defaultDirection = useMemo(
    () => ({
      Folio: true, // Ascendente por defecto
      Fecha: false, // Descendente por defecto (más reciente primero)
      receptor_Nombre: true, // A - Z
      receptor_Rfc: true, // A - Z
      TipoDeComprobante: true, // Orden personalizado: Ingreso, luego Pago, luego Egreso (por defecto)
      Total: false, // Descendente, de mayor a menor
      Estado: true, // Orden personalizado: Pagada, Cancelado, Pendiente (por defecto)
    }),
    []
  );

  const ordenarFacturas = useCallback(
    (facturas) => {
      // Se hace una copia para no mutar el arreglo original
      const arr = [...facturas];
      return arr.sort((a, b) => {
        let result = 0;
        switch (ordenamiento.campo) {
          case "Folio":
            result = Number(a.Folio) - Number(b.Folio);
            break;
          case "Fecha":
            result = new Date(b.Fecha) - new Date(a.Fecha);
            break;
          case "receptor_Nombre":
            result = a.receptor_Nombre.localeCompare(b.receptor_Nombre);
            break;
          case "receptor_Rfc":
            result = a.receptor_Rfc.localeCompare(b.receptor_Rfc);
            break;
          case "TipoDeComprobante": {
            const orderTipo = { Ingreso: 1, Pago: 2, Egreso: 3 };
            result =
              (orderTipo[a.TipoDeComprobante] || 99) -
              (orderTipo[b.TipoDeComprobante] || 99);
            break;
          }
          case "Total":
            result = Number(b.Total) - Number(a.Total);
            break;
          case "Estado": {
            const orderEstado = { Pagada: 1, Cancelado: 2, Pendiente: 3 };
            result =
              (orderEstado[a.estado] || 99) - (orderEstado[b.estado] || 99);
            break;
          }
          default:
            result = 0;
        }
        // Si la dirección actual (ordenamiento.ascendente) NO coincide con la predeterminada, se invierte el resultado
        if (ordenamiento.ascendente !== defaultDirection[ordenamiento.campo]) {
          result = -result;
        }
        return result;
      });
    },
    [ordenamiento, defaultDirection]
  );

  const sortedFacturas = useMemo(() => {
    return ordenarFacturas(filteredFacturasClientes);
  }, [filteredFacturasClientes, ordenarFacturas]);

  // Calcular facturas paginadas
  const paginatedFacturas = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedFacturas.slice(startIndex, endIndex);
  }, [sortedFacturas, currentPage, itemsPerPage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !fileCer ||
      !fileKey ||
      !password ||
      !rfc ||
      !fechaInicial ||
      !fechaFinal
    ) {
      alert("Por favor, llena todos los campos.");
      return;
    }
    const formData = new FormData();
    formData.append("fileCer", fileCer);
    formData.append("fileKey", fileKey);
    formData.append("password", password);
    formData.append("rfc", rfc);
    formData.append("fecha_inicial", fechaInicial);
    formData.append("fecha_final", fechaFinal);
    formData.append("tipo", tipo);
    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/descargar-facturas`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      console.log("Solicitud procesada. Mensaje:", response.data.mensaje);
      if (
        response.data.mensaje &&
        response.data.mensaje.includes("duplicadas")
      ) {
        Swal.fire({
          title: "Factura(s) duplicada(s)",
          icon: "warning",
          confirmButtonText: "Ok",
          scrollbarPadding: false,
        });
      } else {
        Swal.fire({
          title: "Solicitud enviada.",
          text: "Las facturas se actualizarán al completar la descarga.",
          icon: "success",
          confirmButtonText: "Ok",
          scrollbarPadding: false,
        });
      }
      closeModal();
    } catch (error) {
      console.error("Error al enviar la solicitud:", error);
      if (
        error.response &&
        (error.response.status === 408 || error.response.status === 400)
      ) {
        const solicitudId = error.response.data.solicitud_id || "";
        if (solicitudId) setSolicitudPendienteId(solicitudId);
        Swal.fire({
          title: "Solicitud enviada",
          text: `Tu solicitud fue enviada, regresa más tarde.${
            solicitudId ? " ID: " + solicitudId : ""
          }`,
          icon: "info",
          confirmButtonText: "Ok",
          scrollbarPadding: false,
        });
      } else {
        alert("Ocurrió un error al procesar la solicitud.");
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleEstado = async (facturaId) => {
    let newEstado;
    if (estadosCeldas[facturaId] === "Pendiente") {
      newEstado = "Pagada";
    } else if (estadosCeldas[facturaId] === "Pagada") {
      newEstado = "Cancelado";
    } else if (estadosCeldas[facturaId] === "Cancelado") {
      newEstado = "Pendiente";
    } else {
      newEstado = "Pendiente";
    }
    setEstadosCeldas((prev) => ({ ...prev, [facturaId]: newEstado }));
    try {
      const facturaDocRef = doc(db, "facturasEmitidas", facturaId);
      await updateDoc(facturaDocRef, { estado: newEstado });
    } catch (error) {
      console.error("Error al actualizar estado:", error);
    }
  };

  const columnLabels = {
    Folio: "Folio",
    Fecha: "Fecha",
    receptor_Nombre: "Razón Social",
    receptor_Rfc: "RFC",
    TipoDeComprobante: "Tipo Comprobante",
    Total: "Total",
    Estado: "Estado",
  };

  const displayColumns = useMemo(
    () => [
      "Folio",
      "Fecha",
      "receptor_Nombre",
      "receptor_Rfc",
      "TipoDeComprobante",
      "Total",
      "Estado",
    ],
    []
  );

  // Función para manejar el cambio de página
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Función para cambiar la cantidad de items por página
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reinicia a la primera página
  };

  return (
    <>
      <Nav
        proximasAVencer={proximasAVencer}
        proximosEventos={proximosEventos}
      />

      <div className="task-filters">
        <div className="header-actions-proyectos">
          <button className="action-button-proyectos">
            <i className="bi bi-funnel-fill"></i> Todos
            <i className="bi bi-chevron-down" style={{ fontSize: "12px" }}></i>
          </button>
          <div className="buscador-pedidos">
            <i className="bi bi-search"></i>
            <input
              type="text"
              className="buscador-input"
              placeholder="Buscar por cualquier campo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="task-filters-right">
          <div>
            {showNuevoButton && (
              <button className="new-task-button" onClick={openModal}>
                Solicitar Facturas
              </button>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={modalOpen}
        onRequestClose={closeModal}
        contentLabel="Descarga de Facturas"
        style={{
          content: customStyles.content,
          overlay: customStyles.overlay,
        }}
      >
        <button onClick={closeModal} className="modal-close">
          <i className="bi bi-x-lg"></i>
        </button>
        <h2 style={{ textAlign: "center", marginBottom: "20px" }}>
          Descarga de facturas
        </h2>
        <form onSubmit={handleSubmit} className="descarga-facturas-form">
          <div className="form-group">
            <label>Archivo CER</label>
            <input
              type="file"
              name="fileCer"
              style={customStyles.fileInput}
              onChange={(e) => setFileCer(e.target.files[0])}
            />
          </div>
          <div className="form-group">
            <label>Archivo KEY</label>
            <input
              type="file"
              name="fileKey"
              style={customStyles.fileInput}
              onChange={(e) => setFileKey(e.target.files[0])}
            />
          </div>
          <div className="form-group">
            <label>Contraseña de la FIEL</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={customStyles.input}
            />
          </div>
          <div className="form-group">
            <label>RFC</label>
            <input
              type="text"
              value={rfc}
              onChange={(e) => setRfc(e.target.value)}
              placeholder="Ingrese RFC"
              style={customStyles.input}
            />
          </div>
          <div className="form-group">
            <label>Fecha Inicial</label>
            <input
              type="date"
              value={fechaInicial}
              onChange={(e) => setFechaInicial(e.target.value)}
              style={customStyles.input}
            />
          </div>
          <div className="form-group">
            <label>Fecha Final</label>
            <input
              type="date"
              value={fechaFinal}
              onChange={(e) => setFechaFinal(e.target.value)}
              style={customStyles.input}
            />
          </div>
          <div className="form-group">
            <label>Tipo de Factura</label>
            <select
              value={tipo}
              onChange={(e) => setTipo(e.target.value)}
              style={customStyles.select}
            >
              <option value="Emitidas">Emitidas</option>
              {/* <option value="Recibidas">Recibidas</option> */}
            </select>
          </div>
          <button
            type="submit"
            className="generar-archivo-btn"
            style={customStyles.button}
          >
            Solicitar Facturas
          </button>
        </form>
        {loading && (
          <p style={{ textAlign: "center", marginTop: "10px" }}>
            Procesando solicitud, por favor espere...
          </p>
        )}
      </Modal>

      <div className="cotizaciones-table">
        {loadingFacturas ? (
          <Spinner />
        ) : filteredFacturasClientes.length === 0 ? (
          <div className="no-resultados">
            <div
              className="mensaje-sin-resultados"
              style={{ marginTop: "30px" }}
            >
              <p style={{ fontSize: "14px" }}>
                No existe ninguna factura que coincida con esta búsqueda.
              </p>
            </div>
            <div className="imagen-sin-resultados">
              <img
                src="/img/noSearch.svg"
                alt="Sin resultados"
                className="sin-resultados-imagen"
              />
            </div>
          </div>
        ) : (
          <div className="tabla-cotizaciones-wrapper">
            <div className="tabla-cotizaciones-content">
              <table>
                <thead>
                  <tr>
                    {displayColumns.map((col, index) => (
                      <th
                        key={index}
                        onClick={() => handleOrdenamientoChange(col)}
                        style={{ cursor: "pointer" }}
                      >
                        {columnLabels[col] || col}
                        {ordenamiento.campo === col && ordenamiento.ascendente}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedFacturas.map((factura, index) => (
                    <tr
                      key={index}
                      onClick={() => openDetalleModal(factura)}
                      style={{ cursor: "pointer" }}
                    >
                      {displayColumns.map((col, i) => {
                        if (col === "Estado") {
                          return (
                            <td key={i}>
                              <div
                                className={`status ${
                                  estadosCeldas[factura.id] === "Pendiente"
                                    ? "pendiente"
                                    : estadosCeldas[factura.id] === "Pagada"
                                    ? "pagada"
                                    : estadosCeldas[factura.id] === "Cancelado"
                                    ? "cancelado"
                                    : "pendiente"
                                }`}
                                onClick={() => toggleEstado(factura.id)}
                                style={{ cursor: "pointer" }}
                              >
                                {estadosCeldas[factura.id] || "Pendiente"}
                              </div>
                            </td>
                          );
                        }
                        if (col === "Fecha") {
                          return (
                            <td key={i}>
                              <div className="estilos-fechas">
                                {formatDate(factura[col])}
                              </div>
                            </td>
                          );
                        }
                        if (col === "TipoDeComprobante") {
                          return (
                            <td key={i}>
                              {factura[col] === "I"
                                ? "Ingreso"
                                : factura[col] === "E"
                                ? "Egreso"
                                : factura[col] === "P"
                                ? "Pago"
                                : factura[col]}
                            </td>
                          );
                        }
                        if (col === "Total") {
                          const totalValue = parseFloat(factura[col]) || 0;
                          const moneda = factura.Moneda || "";
                          const totalFormateado = new Intl.NumberFormat(
                            "en-US",
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          ).format(totalValue);
                          return (
                            <td key={i}>
                              {totalValue !== 0
                                ? `${moneda} ${totalFormateado}`
                                : totalFormateado}
                            </td>
                          );
                        }
                        return (
                          <td key={i}>
                            {typeof factura[col] === "object"
                              ? JSON.stringify(factura[col])
                              : factura[col]}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="tabla-cotizaciones-footer">
              <div className="paginador-container">
                <Paginador
                  totalItems={filteredFacturasClientes.length}
                  itemsPerPage={itemsPerPage}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleItemsPerPageChange}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <FacturaDetallesModal
        isOpen={detalleModalOpen}
        onRequestClose={closeDetalleModal}
        factura={selectedFactura}
      />
    </>
  );
};

export default FacturasClientes;

